import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Copy, ExternalLink, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/lib/wallet";
import { payUsdc, USDC_MINT } from "@/lib/pay";
import { Logo } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fmtUnits = (units) => (Number(units) / 1e6).toLocaleString("en-US", { maximumFractionDigits: 6 });

export function usePaymentStatus(paymentId, onConfirmed) {
  const [payment, setPayment] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!paymentId) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/payments/${paymentId}`);
        if (r.status === 404) {
          setNotFound(true);
          return;
        }
        const d = await r.json();
        if (!stop) setPayment(d);
      } catch (e) {
        // network hiccup — keep polling
      }
    };
    tick();
    return () => {
      stop = true;
    };
  }, [paymentId]);

  useEffect(() => {
    if (!payment || ["confirmed", "failed"].includes(payment.status)) return;
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`${API}/payments/${payment.id}`);
        const d = await r.json();
        setPayment((prev) => (prev && prev.status === d.status ? prev : d));
        if (d.status === "confirmed") onConfirmed?.(d);
      } catch (e) {
        // keep polling
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [payment?.id, payment?.status]);

  return { payment, setPayment, notFound };
}

export function CopyRow({ text, label }) {
  const copy = () => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed")
    );
  };
  return (
    <button
      onClick={copy}
      data-testid="payment-link-copy"
      className="group flex w-full items-center gap-2.5 rounded-lg border border-line bg-card2 px-4 py-3 text-left transition-colors hover:border-sgreen/40"
    >
      <span className="truncate font-mono text-xs text-white/80">{text}</span>
      <Copy className="ml-auto h-4 w-4 shrink-0 text-mut transition-colors group-hover:text-white" />
    </button>
  );
}

export function Row({ k, v, green }) {
  return (
    <div className="flex items-center justify-between border-b border-line/70 py-2.5 font-mono text-sm">
      <span className="text-mut">{k}</span>
      <span className={green ? "text-sgreen" : "text-white"}>{v}</span>
    </div>
  );
}

export default function PayFlow({ paymentId, brand = false }) {
  const { wallet, address } = useWallet();
  const { payment, setPayment, notFound } = usePaymentStatus(paymentId, () =>
    toast.success("Payment confirmed on Solana")
  );
  const [paying, setPaying] = useState(false);

  const pay = async () => {
    if (!wallet?.provider || !address) return;
    setPaying(true);
    try {
      const signature = await payUsdc({
        provider: wallet.provider,
        payer: address,
        merchantWallet: payment.merchant_wallet,
        merchantUnits: BigInt(payment.merchant_units),
        feeUnits: BigInt(payment.fee_units),
      });
      const r = await fetch(`${API}/payments/${payment.id}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature, payer_wallet: address }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not submit payment");
      setPayment(d);
      toast.success("Transaction submitted", { description: "Confirming on Solana…" });
    } catch (e) {
      toast.error(e?.message?.slice(0, 120) || "Payment failed");
    }
    setPaying(false);
  };

  const [rechecking, setRechecking] = useState(false);
  const recheck = async () => {
    if (!payment?.id || rechecking) return;
    setRechecking(true);
    try {
      const r = await fetch(`${API}/payments/${payment.id}`);
      const d = await r.json();
      if (r.ok) {
        setPayment(d);
        if (d.status !== "failed") toast.success("Found on-chain — updating");
        else toast.info("Still not on-chain. If you just paid, wait a minute and check again.");
      }
    } catch (e) {}
    setRechecking(false);
  };

  if (notFound) {
    return (
      <div className="rounded-2xl border border-line bg-card2 p-10 text-center" data-testid="payflow-not-found">
        <p className="text-lg font-semibold text-white">Payment not found</p>
        <p className="mt-2 text-sm text-mut">This payment link is invalid or was removed.</p>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="flex h-40 items-center justify-center rounded-2xl border border-line bg-card2">
        <Loader2 className="h-6 w-6 animate-spin text-sgreen" />
      </div>
    );
  }

  if (payment.status === "confirmed") {
    return (
      <div className="mx-auto max-w-md text-center" data-testid="payflow-confirmed">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full border border-sgreen/40 bg-sgreen/10">
          <CheckCircle2 className="h-8 w-8 text-sgreen" />
        </div>
        <h3 className="mt-5 text-2xl font-semibold text-white">Payment settled</h3>
        <p className="mt-1.5 font-mono text-xs text-mut">
          {fmtUnits(payment.amount_units)} USDC received · {payment.memo || "No memo"}
        </p>
        <div className="mt-6 text-left">
          <Row k="Merchant received" v={`${fmtUnits(payment.merchant_units)} USDC`} />
          <Row k="Platform fee (1%)" v={`${fmtUnits(payment.fee_units)} USDC`} />
          <Row k="Network" v="Solana mainnet" />
        </div>
        <a
          href={`https://solscan.io/tx/${payment.signature}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="payflow-explorer-link"
          className="mt-5 flex items-center justify-center gap-2 rounded-lg border border-line bg-card2 px-4 py-3 font-mono text-xs text-sgreen transition-colors hover:border-sgreen/40"
        >
          {payment.signature?.slice(0, 24)}… <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    );
  }

  const confirmedSoFar = payment.status === "submitted";
  const failed = payment.status === "failed";

  return (
    <div data-testid="payflow-panel">
      {brand && (
        <div className="flex items-center justify-between border-b border-line pb-5">
          <Logo className="h-8 w-20" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-mut">USDC · {USDC_MINT.slice(0, 6)}…</span>
        </div>
      )}
      <p className="mt-6 font-mono text-4xl font-semibold text-white">
        {fmtUnits(payment.amount_units)}
        <span className="ml-2 text-sm font-medium text-sgreen">USDC</span>
      </p>
      <p className="mt-2 font-mono text-xs text-mut">
        To: {payment.merchant_wallet.slice(0, 8)}…{payment.merchant_wallet.slice(-4)}
        {payment.memo ? ` · ${payment.memo}` : ""}
      </p>
      <div className="mt-5">
        <Row k="Merchant receives (99%)" v={`${fmtUnits(payment.merchant_units)} USDC`} />
        <Row k="Platform fee (1%)" v={`${fmtUnits(payment.fee_units)} USDC`} />
        <Row k="Network" v="Solana mainnet" />
      </div>

      {confirmedSoFar ? (
        <div className="mt-7 flex items-center justify-center gap-3 rounded-xl border border-sgreen/30 bg-sgreen/[0.06] py-4" data-testid="payflow-confirming">
          <Loader2 className="h-4 w-4 animate-spin text-sgreen" />
          <span className="font-mono text-sm text-sgreen">Confirming on Solana…</span>
        </div>
      ) : address ? (
        <button
          onClick={pay}
          disabled={paying}
          data-testid="payflow-pay-button"
          className="cta-glow mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-4 text-sm font-semibold text-black disabled:opacity-50"
        >
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
          {paying ? "Approve in wallet…" : `Pay ${fmtUnits(payment.amount_units)} USDC`}
        </button>
      ) : (
        <div className="mt-7 rounded-xl border border-line bg-card2 p-5 text-center" data-testid="payflow-connect-gate">
          <p className="text-sm text-mut">Connect a Solana wallet to pay with USDC.</p>
          <p className="mt-2 font-mono text-[11px] text-mut/70">Phantom and Solflare are supported.</p>
        </div>
      )}
      {confirmedSoFar && payment.signature && (
        <p className="mt-4 truncate text-center font-mono text-[11px] text-mut">
          TX: {payment.signature}
        </p>
      )}
      {failed && (
        <div className="mt-3 rounded-xl border border-red-400/25 bg-red-400/[0.05] p-4" data-testid="payflow-failed-note">
          <p className="text-center font-mono text-xs leading-relaxed text-red-400">
            {payment.fail_reason || "That transaction didn't land on-chain — you can pay again safely."}
          </p>
          <div className="mt-3 flex items-center justify-center gap-5">
            <button
              onClick={recheck}
              disabled={rechecking}
              data-testid="payflow-recheck-button"
              className="inline-flex items-center gap-1.5 font-mono text-xs text-white transition-colors hover:text-sgreen disabled:opacity-50"
            >
              {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Check again
            </button>
            {payment.signature && (
              <a
                href={`https://solscan.io/tx/${payment.signature}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="payflow-failed-explorer-link"
                className="inline-flex items-center gap-1.5 font-mono text-xs text-mut transition-colors hover:text-sgreen"
              >
                View on Solscan <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
